import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";

export type LogItem = {
  severity: string;
  message: string;
  time: string;
};

const CHUNK_SIZE = 1000;
const ROW_HEIGHT = 18;

const SEVERITY_CLASS: Record<string, string> = {
  error: "log-severity-error",
  warn: "log-severity-warn",
  info: "log-severity-info",
};

const severityClass = (severity: string) => SEVERITY_CLASS[severity] ?? "log-severity-default";

interface LogRowProps {
  line: LogItem;
  lineNumber: number;
  lineNumWidth: number;
}

const LogRow = memo(function LogRow({ line, lineNumber, lineNumWidth }: LogRowProps) {
  return (
    <div className="flex items-center" style={{ height: ROW_HEIGHT }}>
      <span className="log-line-number pr-4 text-right" style={{ width: lineNumWidth, minWidth: lineNumWidth }}>
        {lineNumber}
      </span>
      <span className="w-[60px] min-w-[60px] pl-2.5">{line.time}</span>
      <span className={cn("w-[50px] min-w-[50px] pl-4", severityClass(line.severity))}>{line.severity}</span>
      <span className="whitespace-nowrap pl-4">{line.message}</span>
    </div>
  );
});

interface LogViewerProps {
  data: LogItem[];
  logSource: string;
}

export function LogViewer({ data, logSource }: LogViewerProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const initialScrollDone = useRef(false);
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);

  const totalCount = data.length;
  const startIndex = Math.max(0, totalCount - visibleCount);
  const visibleData = data.slice(startIndex);
  const hasMore = startIndex > 0;

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: visibleData.length + (hasMore ? 1 : 0),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  });

  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  // Auto-scroll to bottom on initial data load and when new logs arrive (if near bottom)
  useEffect(() => {
    if (data.length === 0) return;

    if (!initialScrollDone.current) {
      virtualizer.scrollToIndex(visibleData.length - 1, { align: "end" });
      initialScrollDone.current = true;
    } else if (isNearBottom()) {
      virtualizer.scrollToIndex(visibleData.length - 1, { align: "end" });
    }
  }, [data.length, isNearBottom, virtualizer, visibleData.length]);

  // Maintain scroll position when prepending rows
  useLayoutEffect(() => {
    if (prevScrollHeightRef.current > 0 && scrollRef.current) {
      const diff = scrollRef.current.scrollHeight - prevScrollHeightRef.current;
      scrollRef.current.scrollTop += diff;
      prevScrollHeightRef.current = 0;
    }
  });

  const loadMore = useCallback(() => {
    if (!hasMore || !scrollRef.current) return;
    prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    setVisibleCount((prev) => prev + CHUNK_SIZE);
  }, [hasMore]);

  // Detect scroll to top for loading more
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore) return;

    const handleScroll = () => {
      if (el.scrollTop < ROW_HEIGHT * 5) {
        loadMore();
      }
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadMore]);

  // Reset visible count when data source changes
  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
    initialScrollDone.current = false;
  }, [logSource]);

  const lineNumWidth = `${totalCount}`.length * 8 + 4;
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="mx-3">
      <div
        ref={scrollRef}
        className="log-console my-3 max-h-[800px] overflow-auto rounded-[var(--radius-xs)] px-1 py-2 font-mono text-[12px] leading-none"
      >
        <div className="relative w-fit min-w-full" style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((virtualRow) => {
            const dataIndex = virtualRow.index - (hasMore ? 1 : 0);

            if (hasMore && virtualRow.index === 0) {
              return (
                <div
                  key="sentinel"
                  className="absolute left-0 top-0 flex w-full items-center justify-center"
                  style={{ height: ROW_HEIGHT, transform: `translateY(${virtualRow.start}px)` }}
                >
                  <Loader2 className="log-line-number h-3.5 w-3.5 animate-spin" />
                </div>
              );
            }

            const line = visibleData[dataIndex];
            if (!line) return null;

            return (
              <div
                key={startIndex + dataIndex}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <LogRow line={line} lineNumber={startIndex + dataIndex + 1} lineNumWidth={lineNumWidth} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end">
        <span className="text-[11px] text-fg-3">
          {t("ui.logSource")}: {logSource}
        </span>
      </div>
    </div>
  );
}
